/** Per-overage cost in USD cents (used server-side for billing calculations) */
export const AI_CONVERSATION_OVERAGE_CENTS = 80; // $0.80 per conversation overage (beyond included + add-on)

// ─── SMS / WhatsApp texting add-on ────────────────────────────────────────────
export const SMS_NUMBER_FEE_CENTS  = 600; // $6.00 / number / month
export const SMS_PER_MESSAGE_CENTS = 5;   // $0.05 / message (inbound + outbound, SMS + WhatsApp)

/**
 * Per-org messaging compliance & A2P 10DLC registration surcharge.
 * Charged once per org that has texting enabled (≥1 active number), on top of
 * the per-number and per-message fees. Covers the recurring TCR campaign fee
 * (~$10/mo per client) plus brand/campaign registration and carrier overhead.
 * See docs/sms-compliance-10dlc-dnc-decision.md.
 */
export const MESSAGING_COMPLIANCE_FEE_CENTS = 3000; // $30.00 / org / month
/** Stripe price env var to wire when billing this as a subscription item (see handoff notes). */
export const MESSAGING_COMPLIANCE_STRIPE_PRICE_ENV = 'STRIPE_PRICE_MESSAGING_COMPLIANCE';

/**
 * One-time texting setup fee, charged when an org first enables texting
 * (provisions its first number). Covers A2P 10DLC brand + campaign registration
 * setup and onboarding. Billed once, not recurring.
 */
export const MESSAGING_SETUP_FEE_CENTS = 5000; // $50.00 one-time per org
/** Stripe price env var for the one-time setup charge (see handoff notes). */
export const MESSAGING_SETUP_STRIPE_PRICE_ENV = 'STRIPE_PRICE_MESSAGING_SETUP';

// ─── Translation Add-On Pricing ───────────────────────────────────────────────
// Cost anchor (INTERNAL, never expose to customers):
//   ~$0.00055 per translated message using Haiku 4.5 at $1/$5 per M tokens
//   = ~$0.55 per 1,000 messages (TRANSLATION_COST_PER_1K_CENTS)
//   All tiers priced at ~6× cost at full allotment.
export const TRANSLATION_COST_PER_1K_CENTS = 55; // ~$0.55 / 1k msgs — Haiku 4.5 estimate

export const TRANSLATION_TIERS = {
  starter: {
    monthly_price_cents:    1900,  // $19/mo
    included_messages:      5000,
    overage_per_1k_cents:   400,   // $4/1k — above tier markup so marginal msg is most profitable
    stripe_price_env:       'STRIPE_PRICE_TRANSLATION_STARTER',
  },
  growth: {
    monthly_price_cents:    6900,  // $69/mo
    included_messages:      20000,
    overage_per_1k_cents:   400,
    stripe_price_env:       'STRIPE_PRICE_TRANSLATION_GROWTH',
  },
  scale: {
    monthly_price_cents:    16900, // $169/mo
    included_messages:      50000,
    overage_per_1k_cents:   400,
    stripe_price_env:       'STRIPE_PRICE_TRANSLATION_SCALE',
  },
} as const;

export const TRANSLATION_TOPUP_PACKS = {
  small: {
    price_cents:  1800,  // $18 — below $4/1k overage; still ≥500% markup
    messages:     5000,
    per_1k_cents: 360,
  },
  medium: {
    price_cents:  3400,  // $34
    messages:     10000,
    per_1k_cents: 340,
  },
  large: {
    price_cents:  8500,  // $85
    messages:     25000,
    per_1k_cents: 340,
  },
} as const;

/** Top-up packs expire this many months after purchase */
export const TOPUP_EXPIRY_MONTHS = 12;

/** Upgrade suggestion triggers */
export const TRANSLATION_RELOAD_TRIGGER_COUNT = 2; // reload count in one cycle to suggest upgrade
export const TRANSLATION_EARLY_ALLOTMENT_DAY   = 15; // cycle day before which full consumption triggers upgrade

// ─── AI Conversation Add-On Pricing (Tidio-style) ───────────────────────────
// Renewable monthly quota sold on top of any paid base plan.
// Keyed by pack quota (conversations/month).

export const AI_CONVERSATION_ADDON_TIERS = {
  50:   { quota: 50,   price_cents: 3900,  stripe_price_env: 'STRIPE_PRICE_AI_ADDON_50' },
  100:  { quota: 100,  price_cents: 6900,  stripe_price_env: 'STRIPE_PRICE_AI_ADDON_100' },
  150:  { quota: 150,  price_cents: 9900,  stripe_price_env: 'STRIPE_PRICE_AI_ADDON_150' },
  200:  { quota: 200,  price_cents: 12900, stripe_price_env: 'STRIPE_PRICE_AI_ADDON_200' },
  300:  { quota: 300,  price_cents: 17900, stripe_price_env: 'STRIPE_PRICE_AI_ADDON_300' },
  500:  { quota: 500,  price_cents: 26900, stripe_price_env: 'STRIPE_PRICE_AI_ADDON_500' },
  750:  { quota: 750,  price_cents: 37900, stripe_price_env: 'STRIPE_PRICE_AI_ADDON_750' },
  1000: { quota: 1000, price_cents: 47900, stripe_price_env: 'STRIPE_PRICE_AI_ADDON_1000' },
} as const;

export type AiAddonTierKey = keyof typeof AI_CONVERSATION_ADDON_TIERS;

export const PLAN_LIMITS = {
  free: {
    ai_conversations_monthly: 0,
    included_ai_conversations: 0,
    max_total_ai_conversations: 0 as number | null,
    human_conversations_monthly: 500,
    max_sites: 1 as number | null,
    per_site_identity: false,
    max_ai_agents: 1 as number | null,
    agent_seats: 1,
    max_flows: 1,
    max_kb_docs: 0,
    site_import_pages: 6,
    max_channels: 0,
    max_tickets_monthly: 25,
    max_bot_rules: 5,
    screen_recording: false,
    ai_features: false,
    typing_intelligence: false,
    ticket_deflection: false,
    smart_routing: false,
    customer_portal: false,
    team_chat: false,
    weekly_reports: false,
    crm_summaries: false,
    revenue_attribution: false,
    lead_capture: false,
    industry_intelligence: false,
    health_scoring: false,
    nps_surveys: false,
    customer_segments: false,
    ai_quality_scoring: false,
    agent_coaching: false,
    accessibility_audit: false,
    sla_escalation_trees: false,
    channels: false,
    white_label: false,
    reseller: false,
    mcp_access: false,
    file_video: false,
    file_ai_analysis: false,
    max_file_size_mb: 0,  // No file attachments on free plan
    remove_branding: false,
    conversation_merge: false,
    proactive_engagement: false,
    multilingual_ai: false,
    in_chat_payments: false,
    sentiment_alerts: false,
    auto_escalation: false,
    benchmark_reports: false,
    analytics: false,
    sms: false,
    automation_flows: false,
    marketplace: false,
    ai_suggestions: false,
  },
  starter: {
    ai_conversations_monthly: 50,
    included_ai_conversations: 50,
    max_total_ai_conversations: 150 as number | null,
    human_conversations_monthly: 5000,
    max_sites: 1 as number | null,
    per_site_identity: false,
    max_ai_agents: 1 as number | null,
    agent_seats: 3,
    max_flows: 5,
    max_kb_docs: 5,
    site_import_pages: 6,
    max_channels: 0,
    max_tickets_monthly: null,
    max_bot_rules: null,
    screen_recording: true,
    ai_features: true,
    typing_intelligence: true,
    ticket_deflection: true,
    smart_routing: true,
    customer_portal: true,
    team_chat: true,
    weekly_reports: true,
    crm_summaries: true,
    revenue_attribution: false,
    lead_capture: false,
    industry_intelligence: false,
    health_scoring: false,
    nps_surveys: false,
    customer_segments: false,
    ai_quality_scoring: false,
    agent_coaching: false,
    accessibility_audit: false,
    sla_escalation_trees: false,
    channels: false,
    white_label: false,
    reseller: false,
    mcp_access: false,
    file_video: false,
    file_ai_analysis: true,
    max_file_size_mb: 25,
    remove_branding: true,
    conversation_merge: true,
    proactive_engagement: false,
    multilingual_ai: true,
    in_chat_payments: true,
    sentiment_alerts: false,
    auto_escalation: false,
    benchmark_reports: false,
    analytics: true,
    sms: false,
    automation_flows: false,
    marketplace: false,
    ai_suggestions: true,
  },
  growth: {
    ai_conversations_monthly: 150,
    included_ai_conversations: 150,
    max_total_ai_conversations: 400 as number | null,
    human_conversations_monthly: 25000,
    max_sites: 3 as number | null,
    per_site_identity: true,
    max_ai_agents: 3 as number | null,
    agent_seats: 10,
    max_flows: null,
    max_kb_docs: 25,
    site_import_pages: 12,
    max_channels: 3,
    max_tickets_monthly: null,
    max_bot_rules: null,
    screen_recording: true,
    ai_features: true,
    typing_intelligence: true,
    ticket_deflection: true,
    smart_routing: true,
    customer_portal: true,
    team_chat: true,
    weekly_reports: true,
    crm_summaries: true,
    revenue_attribution: true,
    lead_capture: true,
    industry_intelligence: true,
    health_scoring: true,
    nps_surveys: true,
    customer_segments: true,
    ai_quality_scoring: true,
    agent_coaching: true,
    accessibility_audit: true,
    sla_escalation_trees: true,
    channels: true,
    white_label: false,
    reseller: false,
    mcp_access: false,
    proactive_engagement: true,
    file_video: true,
    file_ai_analysis: true,
    max_file_size_mb: 500,
    remove_branding: true,
    conversation_merge: true,
    multilingual_ai: true,
    in_chat_payments: true,
    sentiment_alerts: true,
    auto_escalation: true,
    benchmark_reports: true,
    analytics: true,
    sms: false,
    automation_flows: true,
    marketplace: true,
    ai_suggestions: true,
  },
  scale: {
    ai_conversations_monthly: 400,
    included_ai_conversations: 400,
    max_total_ai_conversations: 1400 as number | null,
    human_conversations_monthly: 75000,
    max_sites: 10 as number | null,
    per_site_identity: true,
    max_ai_agents: 10 as number | null,
    agent_seats: 15,
    max_flows: null,
    max_kb_docs: 50,
    site_import_pages: 12,
    max_channels: 5,
    max_tickets_monthly: null,
    max_bot_rules: null,
    screen_recording: true,
    ai_features: true,
    typing_intelligence: true,
    ticket_deflection: true,
    smart_routing: true,
    customer_portal: true,
    team_chat: true,
    weekly_reports: true,
    crm_summaries: true,
    revenue_attribution: true,
    lead_capture: true,
    industry_intelligence: true,
    health_scoring: true,
    nps_surveys: true,
    customer_segments: true,
    ai_quality_scoring: true,
    agent_coaching: true,
    accessibility_audit: true,
    sla_escalation_trees: true,
    channels: true,
    white_label: false,
    reseller: false,
    mcp_access: false,
    proactive_engagement: true,
    file_video: true,
    file_ai_analysis: true,
    max_file_size_mb: 500,
    remove_branding: true,
    conversation_merge: true,
    multilingual_ai: true,
    in_chat_payments: true,
    sentiment_alerts: true,
    auto_escalation: true,
    benchmark_reports: true,
    analytics: true,
    sms: false,
    automation_flows: true,
    marketplace: true,
    ai_suggestions: true,
  },
  enterprise: {
    ai_conversations_monthly: 1000,
    included_ai_conversations: 1000,
    max_total_ai_conversations: null as number | null,
    human_conversations_monthly: null,
    max_sites: null as number | null,
    per_site_identity: true,
    max_ai_agents: null as number | null,
    agent_seats: null,
    max_flows: null,
    max_kb_docs: null,
    site_import_pages: 12,
    max_channels: null,
    max_tickets_monthly: null,
    max_bot_rules: null,
    screen_recording: true,
    ai_features: true,
    typing_intelligence: true,
    ticket_deflection: true,
    smart_routing: true,
    customer_portal: true,
    team_chat: true,
    weekly_reports: true,
    crm_summaries: true,
    revenue_attribution: true,
    lead_capture: true,
    industry_intelligence: true,
    health_scoring: true,
    nps_surveys: true,
    customer_segments: true,
    ai_quality_scoring: true,
    agent_coaching: true,
    accessibility_audit: true,
    sla_escalation_trees: true,
    channels: true,
    proactive_engagement: true,
    white_label: true,
    reseller: true,
    mcp_access: true,
    file_video: true,
    file_ai_analysis: true,
    max_file_size_mb: 500,
    remove_branding: true,
    conversation_merge: true,
    multilingual_ai: true,
    in_chat_payments: true,
    sentiment_alerts: true,
    auto_escalation: true,
    benchmark_reports: true,
    analytics: true,
    sms: true,
    automation_flows: true,
    marketplace: true,
    ai_suggestions: true,
  },
} as const;

export type PlanKey = keyof typeof PLAN_LIMITS;
export type PlanFeature = keyof (typeof PLAN_LIMITS)['free'];

// ─── Upgrade-vs-Add-on Recommender ──────────────────────────────────────────
// Derived from PLAN_LIMITS and AI_CONVERSATION_ADDON_TIERS — single source of truth.

export const PLANS = [
  { name: "Starter" as const, price: 29,  includedAI: PLAN_LIMITS.starter.included_ai_conversations, maxTotalAI: PLAN_LIMITS.starter.max_total_ai_conversations! },
  { name: "Growth"  as const, price: 79,  includedAI: PLAN_LIMITS.growth.included_ai_conversations,  maxTotalAI: PLAN_LIMITS.growth.max_total_ai_conversations! },
  { name: "Scale"   as const, price: 199, includedAI: PLAN_LIMITS.scale.included_ai_conversations,   maxTotalAI: PLAN_LIMITS.scale.max_total_ai_conversations! },
] as const;

export const AI_ADDON: [number, number][] = Object.values(AI_CONVERSATION_ADDON_TIERS).map(
  t => [t.quota, t.price_cents / 100] as [number, number],
);

export const addonCost = (extra: number): number | null =>
  extra <= 0 ? 0 : (AI_ADDON.find(([q]) => q >= extra)?.[1] ?? null);

export function totalCost(plan: typeof PLANS[number], volume: number): number | null {
  if (volume > plan.maxTotalAI) return null;
  const a = addonCost(Math.max(0, volume - plan.includedAI));
  return a === null ? null : plan.price + a;
}

export function recommend(volume: number) {
  const options = (PLANS as readonly typeof PLANS[number][])
    .map(p => ({ plan: p.name as string, cost: totalCost(p, volume) }))
    .filter((o): o is { plan: string; cost: number } => o.cost !== null)
    .sort((a, b) => a.cost - b.cost);
  if (!options.length) return { plan: "Enterprise" as string, cost: null as number | null, alternatives: [] as { plan: string; cost: number }[] };
  return { ...options[0], alternatives: options.slice(1) };
}

export const ALLOWED_FILE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp',
  'pdf', 'docx', 'xlsx', 'txt',
  'mp4', 'webm', 'mov',
] as const;

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'] as const;
export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov'] as const;
export const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'xlsx', 'txt'] as const;
export const BLOCKED_EXTENSIONS = [
  'svg', 'zip', 'rar', '7z', 'exe', 'bat',
  'sh', 'js', 'ts', 'php', 'py',
] as const;
